import { useState, useEffect, useRef } from 'react'
import useAxiosPrivate from '../../hooks/useAxiosPrivate'
import MainTitle from '../../components/MainTitle'
import { Navigate, useParams } from 'react-router-dom'
import useAuth from '../../hooks/useAuth'
import moment from 'moment'
import {
  faPenToSquare,
  faUpload,
  faXmarkCircle,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { PhoneNumberUtil } from 'google-libphonenumber'
import AutoComplete from 'react-google-autocomplete'
import { Switch } from '@headlessui/react'
import TCBanner from './TCBanner'
import SchoolNav from '../../components/SchoolNav'

const GOOGLE_API_KEY = 'AIzaSyDcTd3xEx_Y4ZlRo-7eeoSWzLUnWO67LFU'

function School() {

  const errRef = useRef()
  
  const { id } = useParams()
  const { setShowLoader } = useAuth()
  const axios = useAxiosPrivate()
  const [schoolFound, setSchoolFound] = useState(true)
  const [schoolData, setSchoolData] = useState({
    name: '',
    reference: 'WBA-1000',
  })
  const [contactMethods, setContactMethods] = useState([])
  const isMobile = window.innerWidth < 768
  const [loading, setLoading] = useState(false)

  const [editContactMethod, setEditContactMethod] = useState([])
  const [phoneNumber, setPhoneNumber] = useState('')

  const [enabled, setEnabled] = useState(true)
  const [editMode, setEditMode] = useState(false)
  const [editSchoolData, setEditSchoolData] = useState({})
  const [editSchoolDetails, setEditSchoolDetails] = useState({})
  const [editSchoolAddress, setEditSchoolAddress] = useState({})
  const [editSchoolContactName, setEditSchoolContactName] = useState('')

  const [isValid, setIsValid] = useState(true)
  const [errMsg, setErrMsg] = useState('')

  useEffect(() => {
    getSchoolById()
    getContactMethods()
  }, [id])

  const handleDetailsChange = (fieldName, e) => {
    setEditSchoolDetails({ ...editSchoolDetails, [fieldName]: e.target.value })
  }

  const handleContactDetailsChange = (e) => {
    setEditSchoolContactName(e.target.value);
  }

  const handleSchoolPlaceSelected = (place) => {
    const addressFormatted = place.formatted_address
    const segments = place.address_components
    let data = {}
    segments.forEach((key) => {
      const type = key.types[0]
      data[type] = key.long_name
    })

    const formattedSegments = addressFormatted.split(', ')

    const address = {
      address_line_1: data['street_number']
        ? data['street_number'] + ' ' + data['route']
        : formattedSegments[1],
      address_line_2: data['locality'] ?? data['political'],
      city: data['postal_town'],
      county: data['administrative_area_level_2'],
      country: data['country'],
      postcode: data['postal_code'],
    }

    setEditSchoolAddress(address)
    updateSchoolAddress(address)
  }

  const getContactMethods = async () => {
    try {
      const response = await axios.get(`/contact-methods/all`)
      setContactMethods(response.data.result)
    } catch (error) {}
  }

  const getSchoolById = async () => {
    try {
      setShowLoader(true)
      setLoading(true)

      const response = await axios.get(`/schools/${id}`)

      if (!response.data.error) {
        await setSchoolData(response.data.result)

        setEnabled(response.data.result.client_details?.send_notifications)
        setEditSchoolContactName(response.data.result.client_details?.school_contact_name)

        const contactMethodsArray = Object.values(
          response.data.result.contact_methods
        )

        setEditContactMethod(contactMethodsArray)
        setSchoolFound(true)
      } else {
        setSchoolFound(false)
      }
      setLoading(false)
      setShowLoader(false)
    } catch (error) {
      setSchoolFound(false)
    }
  }

  const handleSaveClick = async () => {
    let data = editSchoolData
    data.client_details = editSchoolDetails
    data.client_details.school_contact_name = editSchoolContactName
    data.send_notifications = enabled

    const contactMethodIds = editContactMethod.map((method) => method.id)
    data.contact_method_ids = contactMethodIds

    try {
      setShowLoader(true)
      await axios.post(`/schools/${id}/edit-details`, data)
      .then((response) => {
        if (!response.data.error) {
          setSchoolData(response.data.result)
          setEditMode(false)
          // window.location.reload()
        } else {
          setErrMsg(response.data.result)
          errRef.current.focus()
        }
      })
    } catch (error) {
      console.error(error)
    } finally {
      setShowLoader(false)
    }
    setShowLoader(false)
  }

  useEffect(() => {
    if (schoolData?.client_details?.phone_number) {
      setPhoneNumber(schoolData.client_details?.phone_number)
    }
    if (schoolData?.client_details) {
      setEnabled(schoolData.client_details?.send_notifications === 1)
    }
  }, [schoolData])

  const handleEditClick = () => {
    setEditMode((prevState) => !prevState)
    setEditSchoolData({ ...schoolData })
    setEditSchoolDetails({ ...schoolData.client_details })
    setEditContactMethod([...schoolData.contact_methods])
    setIsValid(true)

    setEditSchoolAddress({})
    setErrMsg('')
  }

  const handleContactMethod = (method) => {
    const updatedContactMethods = [...editContactMethod]
    const index = updatedContactMethods.findIndex(
      (contactMethod) => contactMethod.id === method.id
    )

    if (index !== -1) {
      updatedContactMethods.splice(index, 1)
    } else {
      updatedContactMethods.push(method)
    }

    setEditContactMethod(updatedContactMethods)
  }

  const handlePhoneNumberChange = (event) => {
    const { value } = event.target
    setPhoneNumber(value)
    const isValidNumber = validatePhoneNumber(value)
    setIsValid(isValidNumber)
  }

  const validatePhoneNumber = (phoneNumber) => {
    const phoneNumberUtil = PhoneNumberUtil.getInstance()
    try {
      const parsedNumber = phoneNumberUtil.parse(phoneNumber, '+44')
      const isValidNumber = phoneNumberUtil.isValidNumber(parsedNumber)
      return isValidNumber
    } catch (e) {
      console.error('Error parsing phone number:', e)
      return false
    }
  }

  const updateSchoolAddress = (address) => {
    let addressString = ''

    Object.keys(address).forEach((key, index) => {
      if (key === 'value') {
        return
      }

      if (!address[key] || address[key].trim() === '') {
        return
      }

      addressString += address[key]

      if (index !== Object.keys(address).length - 1) {
        addressString += ', '
      }
    })

    setEditSchoolDetails({...editSchoolDetails, 'school_address' : addressString})
  }

  if (!schoolFound) {
    return <Navigate to="/404" replace />
  }

  const handleInputChange = (fieldName, e) => {
    setEditSchoolData({ ...editSchoolData, [fieldName]: e.target.value })
  }

  return (
    <section>
      <MainTitle
      title={`School Client: ${schoolData?.name}`}
      backButton
      />
      <SchoolNav
        active="details"
        school={schoolData}
      />

      {schoolData.id &&
        <section>
          <section className="w-full bg-white rounded-xl shadow-md py-4 px-4 mb-8">
            <h2 className="font-bold text-xl mb-2">Important Information</h2>
            <div className="mb-2 flex flex-col md:flex-row">
              <span className="text-wba-primary font-semibold mr-2 min-w-[200px]">
                Reference:
              </span>
              {schoolData.reference}
            </div>
            <div className="mb-2 flex flex-col md:flex-row">
              <span className="text-wba-primary font-semibold mr-2 min-w-[200px]">
                School URN:
              </span>
              {schoolData.client_details?.school_urn}
            </div>

            <hr className="mt-4 pb-4" />
            {editMode && (
              <div className="py-3 px-4 bg-teal-200 rounded-lg mb-4 text-sm">
                <span className="font-medium">You are in Editing Mode.</span>{' '}
                Click the pen icon to return to viewing or save to update
                details.{' '}
              </div>
            )}
            <div ref={errRef} className={errMsg ? 'errmsg' : 'offscreen'} aria-live='assertive'>
              {errMsg}
            </div>
            <div className="flex flex-col md:flex-row items-center justify-between">
              <h2 className="font-bold text-xl mb-2">Client Details</h2>
              <div>
                {!isMobile && (
                  <FontAwesomeIcon
                    title="Edit Client"
                    onClick={() => {
                      handleEditClick()
                    }}
                    icon={faPenToSquare}
                    className={`text-xl hover:text-blue-500 cursor-pointer ${
                      editMode ? 'text-blue-500' : ''
                    }`}
                  />
                )}
              </div>
            </div>
            <div className="mb-2 flex flex-col md:flex-row">
              <span className="text-wba-primary font-semibold mr-2 min-w-[200px]">
                School Name:
              </span>
              {editMode ? (
                <input
                  type="text"
                  className="border border-slate-400 rounded-lg px-2 py-1 text-sm"
                  defaultValue={editSchoolData.name}
                  onChange={(e) => handleInputChange('name', e)}
                />
              ) : (
                <span>{schoolData.name}</span>
              )}
            </div>
            <div className="mb-2 flex flex-col md:flex-row">
              <span className="text-wba-primary font-semibold mr-2 min-w-[200px]">
                School Address:
              </span>
              {editMode ? (
                <AutoComplete
                  defaultValue={editSchoolDetails?.school_address}
                  type="text"
                  name="client_address"
                  required
                  id="client_address"
                  className="border border-slate-400 rounded-lg px-2 py-1 text-sm min-w-[400px]"
                  apiKey={GOOGLE_API_KEY}
                  onPlaceSelected={(place) =>
                    handleSchoolPlaceSelected(place)
                  }
                  options={{
                    types: [],
                    componentRestrictions: { country: 'gb' },
                  }}
                />
              ) : (
                <span>{schoolData.client_details?.school_address}</span>
              )}
            </div>
            {editMode && editSchoolAddress.address_line_1 && (
              <div className="ml-10 my-4">
                <div className="mb-2 flex flex-col md:flex-row">
                  <span className="text-wba-primary font-semibold mr-2 min-w-[200px]">
                    Address Line 1:
                  </span>
                  <input
                    type="text"
                    className="border border-slate-400 rounded-lg px-2 py-1 text-sm"
                    value={editSchoolAddress.address_line_1 || ''}
                    onChange={(e) => {
                      const address = editSchoolAddress
                      address.address_line_1 = e.target.value
                      setEditSchoolAddress({
                        ...editSchoolAddress,
                        address_line_1: e.target.value,
                      })
                      updateSchoolAddress(address)
                    }}
                  />
                </div>
                <div className="mb-2 flex flex-col md:flex-row">
                  <span className="text-wba-primary font-semibold mr-2 min-w-[200px]">
                    Address Line 2:
                  </span>
                  <input
                    type="text"
                    className="border border-slate-400 rounded-lg px-2 py-1 text-sm"
                    value={editSchoolAddress.address_line_2 || ''}
                    onChange={(e) => {
                      const address = editSchoolAddress
                      address.address_line_2 = e.target.value
                      setEditSchoolAddress({
                        ...editSchoolAddress,
                        address_line_2: e.target.value,
                      })
                      updateSchoolAddress(address)
                    }}
                  />
                </div>
                <div className="mb-2 flex flex-col md:flex-row">
                  <span className="text-wba-primary font-semibold mr-2 min-w-[200px]">
                    City:
                  </span>
                  <input
                    type="text"
                    className="border border-slate-400 rounded-lg px-2 py-1 text-sm"
                    value={editSchoolAddress.city || ''}
                    onChange={(e) => {
                      const address = editSchoolAddress
                      address.city = e.target.value
                      setEditSchoolAddress({
                        ...editSchoolAddress,
                        city: e.target.value,
                      })
                      updateSchoolAddress(address)
                    }}
                  />
                </div>
                <div className="mb-2 flex flex-col md:flex-row">
                  <span className="text-wba-primary font-semibold mr-2 min-w-[200px]">
                    County:
                  </span>
                  <input
                    type="text"
                    className="border border-slate-400 rounded-lg px-2 py-1 text-sm"
                    value={editSchoolAddress.county || ''}
                    onChange={(e) => {
                      const address = editSchoolAddress
                      address.county = e.target.value
                      setEditSchoolAddress({
                        ...editSchoolAddress,
                        county: e.target.value,
                      })
                      updateSchoolAddress(address)
                    }}
                  />
                </div>
                <div className="mb-2 flex flex-col md:flex-row">
                  <span className="text-wba-primary font-semibold mr-2 min-w-[200px]">
                    Country:
                  </span>
                  <input
                    type="text"
                    className="border border-slate-400 rounded-lg px-2 py-1 text-sm"
                    value={editSchoolAddress.country || ''}
                    onChange={(e) => {
                      const address = editSchoolAddress
                      address.country = e.target.value
                      setEditSchoolAddress({
                        ...editSchoolAddress,
                        country: e.target.value,
                      })
                      updateSchoolAddress(address)
                    }}
                  />
                </div>
                <div className="mb-2 flex flex-col md:flex-row">
                  <span className="text-wba-primary font-semibold mr-2 min-w-[200px]">
                    Postcode:
                  </span>
                  <input
                    type="text"
                    className="border border-slate-400 rounded-lg px-2 py-1 text-sm"
                    value={editSchoolAddress.postcode || ''}
                    onChange={(e) => {
                      const address = editSchoolAddress
                      address.postcode = e.target.value
                      setEditSchoolAddress({
                        ...editSchoolAddress,
                        postcode: e.target.value,
                      })
                      updateSchoolAddress(address)
                    }}
                  />
                </div>
              </div>
            )}
            <div className="mb-2 flex flex-col md:flex-row">
              <span className="text-wba-primary font-semibold mr-2 min-w-[200px]">
                Email Address:
              </span>
              {editMode ? (
                <input
                  type="text"
                  className="border border-slate-400 rounded-lg px-2 py-1 text-sm"
                  defaultValue={editSchoolData.email}
                  onChange={(e) => handleInputChange('email', e)}
                />
              ) : (
                <span>{schoolData.email}</span>
              )}
            </div>
            <div className="mb-2 flex flex-col md:flex-row">
              <span className="text-wba-primary font-semibold mr-2 min-w-[200px]">
                Main Contact Name:
              </span>
              <div>
                {editMode ? (
                  <input
                    type="text"
                    className="border border-slate-400 rounded-lg px-2 py-1 text-sm"
                    defaultValue={editSchoolData.client_details?.school_contact_name}
                    onChange={(e) => {
                      handleContactDetailsChange(e)
                    }}
                  />
                ) : (
                  <span>{schoolData.client_details?.school_contact_name}</span>
                )}
              </div>
            </div>
            <div className="mb-2 flex flex-col md:flex-row">
              <span className="text-wba-primary font-semibold mr-2 min-w-[200px]">
                Main Contact Number:
              </span>
              <div>
                {editMode ? (
                  <input
                    type="text"
                    className="border border-slate-400 rounded-lg px-2 py-1 text-sm"
                    defaultValue={editSchoolData.client_details?.phone_number}
                    onChange={(e) => {
                      handlePhoneNumberChange(e)
                      handleDetailsChange('phone_number', e)
                    }}
                  />
                ) : (
                  <span>{schoolData.client_details?.phone_number}</span>
                )}
                <div>
                  {editMode && (
                    <div className="text-left">
                      {phoneNumber.length > 0 && (
                        <>
                          {isValid ? (
                            <span className="text-green-500 text-sm">
                              Valid phone number
                            </span>
                          ) : (
                            <span className="text-red-600 text-sm">
                              Invalid phone number
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="mb-2 flex flex-col md:flex-row">
              <span className="text-wba-primary font-semibold mr-2 min-w-[200px]">
                Assigned Region:
              </span>
              <span>{schoolData.client_details?.region}</span>
            </div>

            <hr className="mt-4 pb-4" />
            <h2 className="font-bold text-xl mb-2">Contact Preferences</h2>
            <div className="">
              <p className="text-wba-primary font-semibold mr-2 min-w-[200px]">
                Preferred Method of Contact:
              </p>
            </div>
            <div className="mb-2 flex flex-col md:flex-row items-center space-x-4">
              <div>
                {editMode ? (
                  <div className="flex">
                    {contactMethods?.map((method) => {
                      return (
                        <div key={method.id} className="items-center flex">
                          <input
                            type="checkbox"
                            id="contact_method"
                            value={method.title}
                            defaultChecked={
                              Array.isArray(editContactMethod) &&
                              editContactMethod
                                .map((e) => e.title)
                                .includes(method.title)
                            }
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded"
                            onChange={() => handleContactMethod(method)}
                          />
                          <label
                            htmlFor="clinic_details"
                            className="ml-2 mr-4 text-sm font-medium text-gray-900 dark:text-gray-300"
                          >
                            {method.title}
                          </label>
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <div>
                    {schoolData.contact_methods.length > 0 && (
                      <div className="flex space-x-4">
                        {schoolData.contact_methods.map((method) => {
                          return <div key={method.id}>{method.title}</div>
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="mb-2 flex flex-col md:flex-row">
              <span className="text-wba-primary font-semibold mr-2 min-w-[200px]">
                Preferred Contact Time:
              </span>
              {editMode ? (
                <input
                  type="text"
                  className="border border-slate-400 rounded-lg px-2 py-1 text-sm"
                  defaultValue={editSchoolDetails.contact_time}
                  onChange={(e) => handleDetailsChange('contact_time', e)}
                />
              ) : (
                <span>{schoolData.client_details?.contact_time}</span>
              )}
            </div>
            <div className="mb-2 flex flex-col md:flex-row">
              <span className="text-wba-primary font-semibold mr-2 min-w-[200px]">
                Send automatic notifications:
              </span>
              {editMode ? (
                <div className="flex items-center">
                  <span className="pr-2 text-gray-700 text-sm">No</span>
                  <Switch
                    checked={enabled}
                    onChange={setEnabled}
                    className="group relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 data-[checked]:bg-blue-500"
                  >
                    <span className="sr-only">Use setting</span>
                    <span className="pointer-events-none relative inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5">
                      <span
                        aria-hidden="true"
                        className="absolute inset-0 flex h-full w-full items-center justify-center transition-opacity duration-200 ease-in group-data-[checked]:opacity-0 group-data-[checked]:duration-100 group-data-[checked]:ease-out"
                      >
                        <svg
                          fill="none"
                          viewBox="0 0 12 12"
                          className="h-3 w-3 text-gray-400"
                        >
                          <path
                            d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2"
                            stroke="currentColor"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                      <span
                        aria-hidden="true"
                        className="absolute inset-0 flex h-full w-full items-center justify-center opacity-0 transition-opacity duration-100 ease-out group-data-[checked]:opacity-100 group-data-[checked]:duration-200 group-data-[checked]:ease-in"
                      >
                        <svg
                          fill="currentColor"
                          viewBox="0 0 12 12"
                          className="h-3 w-3 text-indigo-600"
                        >
                          <path d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z" />
                        </svg>
                      </span>
                    </span>
                  </Switch>
                  <span className="pl-2 text-gray-700">Yes</span>
                </div>
              ) : (
                <div className="flex items-center">
                  <div className="flex items-center">
                    {enabled ? (
                      <span className="pl-2 text-gray-700">Yes</span>
                    ) : (
                      <span className="pr-2 text-gray-700">No</span>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end">
              {editMode && (
                <button
                  onClick={handleSaveClick}
                  className="px-4 py-2 rounded-lg bg-blue-500 text-white hover:bg-blue-700"
                >
                  <FontAwesomeIcon icon={faUpload} />
                  <span className="pl-1">Save</span>
                </button>
              )}
            </div>
          </section>
        </section>
        
      }
    </section>

  )
}

export default School